import {
    STPlaceholderType,
    STShapeType,
    STTextAnchoringType,
    STTextVerticalType,
    STTextAlignType,
    STSchemeColorVal,
} from '@tencent/slide-sdk-pb-model';
import { generateUpperUUID } from '../../utils/uuid-generate';
import { DataType } from '../data';
import type { IShapeData } from '../shape-data';
import type { ICTTransform2D, ICTTextParagraph } from '@tencent/slide-sdk-pb-model';

interface AddPlaceholderOptions {
    placeholderType: AddPlaceholderType;
    placeholderIndex?: number;
    isVert?: boolean; // 竖排占位符
    text?: string[];
    textFieldType?: 'datetime1' | 'slidenum';
}

interface AddMasterPlaceholderOptions extends AddPlaceholderOptions {
    transform2D?: ICTTransform2D;
    hasAnchor?: boolean;
    alignment?: STTextAlignType;
}

export enum AddPlaceholderType {
    Title = 'title',
    Body = 'body',
    Table = 'table',
    Picture = 'picture',
    Date = 'date',
    Footer = 'Footer',
    SlideNumber = 'slideNumber',
    Content = 'content',
}

export type MasterPlaceholderType =
    | AddPlaceholderType.Title
    | AddPlaceholderType.Body
    | AddPlaceholderType.Date
    | AddPlaceholderType.Footer
    | AddPlaceholderType.SlideNumber;

export type LayoutUniquePlaceholderType =
    | AddPlaceholderType.Title
    | AddPlaceholderType.Date
    | AddPlaceholderType.Footer
    | AddPlaceholderType.SlideNumber;

// 母版占位符预设配置
export const MasterPlaceholderOptionsMap = {
    [AddPlaceholderType.Title]: {
        placeholderType: AddPlaceholderType.Title,
        text: ['单击此处编辑母版标题样式'],
        transform2D: { off: { x: 838200, y: 365125 }, ext: { cx: 10515600, cy: 1325563 } },
        hasAnchor: true,
    },
    [AddPlaceholderType.Body]: {
        placeholderType: AddPlaceholderType.Body,
        text: ['单击此处编辑母版文本样式', '二级', '三级', '四级', '五级'],
        transform2D: { off: { x: 838200, y: 1825625 }, ext: { cx: 10515600, cy: 4351338 } },
    },
    [AddPlaceholderType.Date]: {
        placeholderType: AddPlaceholderType.Date,
        text: [''],
        transform2D: { off: { x: 838200, y: 6356350 }, ext: { cx: 2743200, cy: 365125 } },
        hasAnchor: true,
        alignment: STTextAlignType.l,
        textFieldType: 'datetime1' as const,
    },
    [AddPlaceholderType.Footer]: {
        placeholderType: AddPlaceholderType.Footer,
        transform2D: { off: { x: 4038600, y: 6356350 }, ext: { cx: 4114800, cy: 365125 } },
        hasAnchor: true,
        alignment: STTextAlignType.ctr,
    },
    [AddPlaceholderType.SlideNumber]: {
        placeholderType: AddPlaceholderType.SlideNumber,
        text: ['‹#›'],
        transform2D: { off: { x: 8610600, y: 6356350 }, ext: { cx: 2743200, cy: 365125 } },
        hasAnchor: true,
        alignment: STTextAlignType.r,
        textFieldType: 'slidenum' as const,
    },
};

// 版式占位符预设配置
export const LayoutPlaceholderOptionsMap = {
    [AddPlaceholderType.Body]: {
        placeholderType: AddPlaceholderType.Body,
        text: ['单击此处编辑母版文本样式', '二级', '三级', '四级', '五级'],
    },
    [AddPlaceholderType.Content]: {
        placeholderType: AddPlaceholderType.Content,
        text: ['单击此处编辑母版文本样式', '二级', '三级', '四级', '五级'],
    },
    [AddPlaceholderType.Picture]: {
        placeholderType: AddPlaceholderType.Picture,
    },
    [AddPlaceholderType.Table]: {
        placeholderType: AddPlaceholderType.Table,
    },
    [AddPlaceholderType.Title]: {
        placeholderType: AddPlaceholderType.Title,
        text: ['单击此处编辑母版标题样式'],
    },
    [AddPlaceholderType.Date]: {
        placeholderType: AddPlaceholderType.Date,
        text: [''],
        textFieldType: 'datetime1' as const,
    },
    [AddPlaceholderType.Footer]: {
        placeholderType: AddPlaceholderType.Footer,
    },
    [AddPlaceholderType.SlideNumber]: {
        placeholderType: AddPlaceholderType.SlideNumber,
        text: ['‹#›'],
        textFieldType: 'slidenum' as const,
    },
};

export const PLACEHOLDER_TYPE_MAP = {
    [AddPlaceholderType.Title]: STPlaceholderType.title,
    [AddPlaceholderType.Body]: STPlaceholderType.body,
    [AddPlaceholderType.Table]: STPlaceholderType.tbl,
    [AddPlaceholderType.Picture]: STPlaceholderType.pic,
    [AddPlaceholderType.Date]: STPlaceholderType.dt,
    [AddPlaceholderType.Footer]: STPlaceholderType.ftr,
    [AddPlaceholderType.SlideNumber]: STPlaceholderType.sldNum,
};

/**
 * 创建母版占位符的预设数据
 */
export const createMasterPlaceholderData = ({
    placeholderType,
    placeholderIndex,
    text,
    transform2D,
    hasAnchor,
    alignment,
    textFieldType,
}: AddMasterPlaceholderOptions) => {
    const placeholder: IShapeData = {
        id: '',
        type: DataType.Shape,
        spPr: {},
        nvPr: {
            locking: {
                noGrp: true,
            },
        },
    };
    placeholder.spPr.prstGeom = { prst: STShapeType.rect };
    if (transform2D) {
        placeholder.spPr.xfrm = transform2D;
    }
    // 处理占位符属性
    placeholder.nvPr.ph = {};
    if (placeholderType !== AddPlaceholderType.Content) {
        placeholder.nvPr.ph.type = PLACEHOLDER_TYPE_MAP[placeholderType];
    }
    if (placeholderIndex) {
        placeholder.nvPr.ph.idx = placeholderIndex;
    }
    // 处理文本属性
    placeholder.txBody = {};
    // TODO: 添加 normAutoFit
    placeholder.txBody.bodyPr = {
        vert: STTextVerticalType.horz,
        lIns: 91440,
        rIns: 91440,
        tIns: 45720,
        bIns: 45720,
    };
    if (hasAnchor) {
        placeholder.txBody.bodyPr.anchor = STTextAnchoringType.ctr;
    }
    if (alignment) {
        placeholder.txBody.lstStyle = {
            lvl1pPr: {
                lvl: 0,
                algn: alignment,
                defRPr: {
                    sz: 1200,
                    solidFill: {
                        egColorChoice: {
                            schemeClr: { val: STSchemeColorVal.tx1, egColorTransform: [{ tint: { val: 75000 } }] },
                        },
                    },
                },
            },
        };
    }
    placeholder.txBody.p = getTextParagraph(text, textFieldType);
    return placeholder;
};

/**
 * 创建版式占位符的预设数据
 */
export const createLayoutPlaceholderData = ({
    placeholderType,
    placeholderIndex,
    isVert,
    text,
    textFieldType,
}: AddPlaceholderOptions) => {
    const placeholder: IShapeData = {
        id: '',
        type: DataType.Shape,
        spPr: {},
        nvPr: {
            locking: {
                noGrp: true,
            },
        },
    };
    // 处理占位符属性
    placeholder.nvPr.ph = {};
    if (placeholderType !== AddPlaceholderType.Content) {
        placeholder.nvPr.ph.type = PLACEHOLDER_TYPE_MAP[placeholderType];
    }
    if (placeholderIndex) {
        placeholder.nvPr.ph.idx = placeholderIndex;
    }
    // 处理文本属性
    placeholder.txBody = {};
    if (isVert) {
        placeholder.txBody.bodyPr = { vert: STTextVerticalType.eaVert };
    }
    placeholder.txBody.p = getTextParagraph(text, textFieldType);
    return placeholder;
};

const getTextParagraph = (text?: string[], textFieldType?: 'datetime1' | 'slidenum'): ICTTextParagraph[] => {
    if (text && textFieldType) {
        return text.map((item) => {
            const paragraph: ICTTextParagraph = {};
            paragraph.egTextRun = [
                {
                    fld: {
                        id: `{${generateUpperUUID()}}`,
                        rPr: {
                            lang: 'zh-CN',
                        },
                        t: textFieldType === 'datetime1' ? getDateString() : item,
                        type: textFieldType,
                    },
                },
            ];
            return paragraph;
        });
    }
    if (text) {
        return text.map((item, index) => {
            const paragraph: ICTTextParagraph = {};
            paragraph.pPr = { lvl: index };
            paragraph.egTextRun = [
                {
                    r: {
                        rPr: {
                            lang: 'zh-CN',
                        },
                        t: item,
                    },
                },
            ];
            return paragraph;
        });
    }
    return [
        {
            endParaRPr: {
                lang: 'zh-CN',
            },
        },
    ];
};

/**
 * 添加日期占位符时的默认文本
 * 由于文本内容不能为空，故在生成占位符的时候用此文本占位，文本的实际更新维护在上层进行
 * 建议以后添加占位符的数据生成也在上层进行
 */
const getDateString = () => {
    const date = new Date();
    return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};
